import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useDispatch, useSelector} from "react-redux";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Chip,
} from '@material-ui/core';
import {connect, transceive, autoConnect} from "./actions";
import {byteToHexString} from "./util";
import {useSnackbar} from "notistack";
import {useTranslation} from 'react-i18next';
import LinkIcon from '@material-ui/icons/Link';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import CodeIcon from '@material-ui/icons/Code';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  grid: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: '#e3f2fd',
    borderRadius: theme.shape.borderRadius,
  },
}));

const MESSAGE_TYPES = {
  URL: 'url',
  TEXT: 'text',
  CUSTOM: 'custom',
};

export default function Ndef() {
  const classes = useStyles();
  const {t} = useTranslation();
  const device = useSelector(state => state.device);
  const adminAuthenticated = useSelector(state => state.adminAuthenticated);
  const dispatch = useDispatch();
  const {enqueueSnackbar} = useSnackbar();

  const [messageType, setMessageType] = useState(MESSAGE_TYPES.URL);
  const [urlAddress, setUrlAddress] = useState('https://console.griffonoakeys.com/');
  const [textContent, setTextContent] = useState('Griffonoakey');
  const [language, setLanguage] = useState('en');
  const [customHex, setCustomHex] = useState('');
  const [currentMessage, setCurrentMessage] = useState('');
  const [messageSize, setMessageSize] = useState(0);
  const [readOnly, setReadOnly] = useState(false);

  const selectNdefApplet = useCallback(async () => {
    if (device === null) {
      const autoConnected = await dispatch(autoConnect());
      if (!autoConnected) {
        if (!await dispatch(connect())) {
          throw new Error('Cannot connect to device');
        }
      }
    }
    // Select NDEF application
    let res = await dispatch(transceive("00A4040007D2760000850101"));
    if (!res.endsWith("9000")) {
      throw new Error('Selecting NDEF applet failed');
    }
  }, [device, dispatch]);

  const selectAdminApplet = useCallback(async () => {
    if (device === null) {
      const autoConnected = await dispatch(autoConnect());
      if (!autoConnected) {
        if (!await dispatch(connect())) {
          throw new Error('Cannot connect to device');
        }
      }
    }
    let res = await dispatch(transceive("00A4040005F000000000"));
    if (!res.endsWith("9000")) {
      throw new Error('Selecting admin applet failed');
    }
  }, [device, dispatch]);

  const readNdefMessage = useCallback(async () => {
    try {
      await selectNdefApplet();
      
      // Select NDEF file (0x0001)
      let res = await dispatch(transceive("00A4000C020001"));
      if (!res.endsWith("9000")) {
        throw new Error('Selecting NDEF file failed');
      }
      
      // Read first 2 bytes (NLEN)
      res = await dispatch(transceive("00B0000002"));
      if (!res.endsWith("9000")) {
        throw new Error('Reading NDEF length failed');
      }
      
      const nlenHex = res.substring(0, 4);
      const nlen = parseInt(nlenHex, 16);
      
      if (nlen === 0) {
        setCurrentMessage('(Empty)');
        setMessageSize(0);
        enqueueSnackbar(t('msg.operation_success'), {variant: 'success'});
        return;
      }
      
      // Read NDEF message
      const totalLen = nlen + 2; // NLEN (2 bytes) + message
      const chunks = Math.ceil(totalLen / 255);
      let fullData = '';
      
      for (let i = 0; i < chunks; i++) {
        const offset = i * 255;
        const len = Math.min(255, totalLen - offset);
        const offsetHex = byteToHexString([(offset >> 8) & 0xFF, offset & 0xFF]);
        
        res = await dispatch(transceive(`00B0${offsetHex}${byteToHexString([len])}`));
        if (!res.endsWith("9000")) {
          throw new Error(`Reading NDEF chunk ${i + 1} failed`);
        }
        
        fullData += res.substring(0, res.length - 4);
      }
      
      // Parse NDEF message
      const messageHex = fullData.substring(4); // Skip NLEN
      setCurrentMessage(messageHex);
      setMessageSize(nlen);
      
      // Try to decode message
      try {
        const messageBytes = [];
        for (let i = 0; i < messageHex.length; i += 2) {
          messageBytes.push(parseInt(messageHex.substr(i, 2), 16));
        }
        
        // Simple URL record detection (TNF=1, Type=U)
        if (messageBytes[0] === 0xD1 && messageBytes[2] === 0x55) {
          const urlCode = messageBytes[4];
          const urlData = messageBytes.slice(5);
          const urlPrefix = ['http://www.', 'https://www.', 'http://', 'https://'][urlCode] || '';
          const url = urlPrefix + String.fromCharCode(...urlData);
          setUrlAddress(url);
          setMessageType(MESSAGE_TYPES.URL);
        }
        // Text record (TNF=1, Type=T)
        else if (messageBytes[0] === 0xD1 && messageBytes[2] === 0x54) {
          const langLen = messageBytes[4] & 0x3F;
          const text = String.fromCharCode(...messageBytes.slice(5 + langLen));
          setTextContent(text);
          setMessageType(MESSAGE_TYPES.TEXT);
        }
      } catch (e) {
        console.error('Failed to decode NDEF message:', e);
      }
      
      enqueueSnackbar(t('msg.operation_success'), {variant: 'success'});
    } catch (e) {
      enqueueSnackbar(`${t('msg.operation_failed')}: ${e}`, {variant: 'error'});
    }
  }, [dispatch, selectNdefApplet, enqueueSnackbar, t]);

  const writeNdefMessage = useCallback(async () => {
    try {
      await selectNdefApplet();
      
      let ndefMessage = '';
      
      if (messageType === MESSAGE_TYPES.URL) {
        // Create URL NDEF record
        const prefixes = ['http://www.', 'https://www.', 'http://', 'https://'];
        let urlCode = 0;
        let urlBody = urlAddress;
        
        for (let i = 0; i < prefixes.length; i++) {
          if (urlAddress.startsWith(prefixes[i])) {
            urlCode = i;
            urlBody = urlAddress.substring(prefixes[i].length);
            break;
          }
        }
        
        const urlBytes = [];
        for (let i = 0; i < urlBody.length; i++) {
          urlBytes.push(urlBody.charCodeAt(i));
        }
        
        const payloadLen = 1 + urlBytes.length; // URL code + URL body
        ndefMessage = byteToHexString([
          0xD1, // MB=1, ME=1, CF=0, SR=1, IL=0, TNF=1
          0x01, // Type length = 1
          payloadLen, // Payload length
          0x55, // Type = 'U'
          urlCode, // URL prefix code
          ...urlBytes
        ]);
      } else if (messageType === MESSAGE_TYPES.TEXT) {
        // Create Text NDEF record
        const langBytes = [];
        for (let i = 0; i < language.length; i++) {
          langBytes.push(language.charCodeAt(i));
        }
        
        const textBytes = [];
        for (let i = 0; i < textContent.length; i++) {
          textBytes.push(textContent.charCodeAt(i));
        }
        
        const payloadLen = 1 + langBytes.length + textBytes.length;
        ndefMessage = byteToHexString([
          0xD1, // MB=1, ME=1, CF=0, SR=1, IL=0, TNF=1
          0x01, // Type length = 1
          payloadLen, // Payload length
          0x54, // Type = 'T'
          langBytes.length, // Language code length (UTF-8)
          ...langBytes,
          ...textBytes
        ]);
      } else if (messageType === MESSAGE_TYPES.CUSTOM) {
        ndefMessage = customHex.replace(/[^0-9A-Fa-f]/g, '');
      }
      
      if (ndefMessage.length > 2044) { // 1022 * 2 (hex string)
        throw new Error('Message too large (max 1022 bytes)');
      }
      
      // Select NDEF file
      let res = await dispatch(transceive("00A4000C020001"));
      if (!res.endsWith("9000")) {
        throw new Error('Selecting NDEF file failed');
      }
      
      // Prepare data: NLEN + NDEF message
      const nlen = ndefMessage.length / 2;
      const fullData = byteToHexString([(nlen >> 8) & 0xFF, nlen & 0xFF]) + ndefMessage;
      
      // Write in chunks
      const chunks = Math.ceil(fullData.length / (255 * 2));
      for (let i = 0; i < chunks; i++) {
        const offset = i * 255;
        const chunkData = fullData.substring(i * 510, Math.min((i + 1) * 510, fullData.length));
        const chunkLen = chunkData.length / 2;
        const offsetHex = byteToHexString([(offset >> 8) & 0xFF, offset & 0xFF]);
        
        res = await dispatch(transceive(`00D6${offsetHex}${byteToHexString([chunkLen])}${chunkData}`));
        if (!res.endsWith("9000")) {
          throw new Error(`Writing NDEF chunk ${i + 1} failed`);
        }
      }
      
      await readNdefMessage();
      enqueueSnackbar(t('msg.operation_success'), {variant: 'success'});
    } catch (e) {
      enqueueSnackbar(`${t('msg.operation_failed')}: ${e}`, {variant: 'error'});
    }
  }, [messageType, urlAddress, textContent, language, customHex, dispatch, selectNdefApplet, readNdefMessage, enqueueSnackbar, t]);

  const toggleReadOnly = useCallback(async () => {
    try {
      await selectAdminApplet();
      const p1 = readOnly ? '00' : '01';
      let res = await dispatch(transceive(`0008${p1}00`));
      if (!res.endsWith("9000")) {
        throw new Error('Toggle read-only failed');
      }
      
      setReadOnly(!readOnly);
      enqueueSnackbar(t('msg.operation_success'), {variant: 'success'});
    } catch (e) {
      enqueueSnackbar(`${t('msg.operation_failed')}: ${e}`, {variant: 'error'});
    }
  }, [readOnly, dispatch, selectAdminApplet, enqueueSnackbar, t]);

  const resetNdef = useCallback(async () => {
    if (!window.confirm(t('ndef.reset_confirm'))) {
      return;
    }
    
    try {
      await selectAdminApplet();
      let res = await dispatch(transceive("0007000000"));
      if (!res.endsWith("9000")) {
        throw new Error('Reset NDEF failed');
      }
      
      await readNdefMessage();
      enqueueSnackbar(t('msg.operation_success'), {variant: 'success'});
    } catch (e) {
      enqueueSnackbar(`${t('msg.operation_failed')}: ${e}`, {variant: 'error'});
    }
  }, [dispatch, selectAdminApplet, readNdefMessage, enqueueSnackbar, t]);

  useEffect(() => {
    if (device && adminAuthenticated) {
      console.log('✅ Device connected and Admin authenticated, reading NDEF message');
      readNdefMessage();
    } else if (device && !adminAuthenticated) {
      console.log('⚠️ Device connected but Admin not authenticated, waiting...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, adminAuthenticated]); // 依赖 device 和 adminAuthenticated

  return (
    <div className={classes.root}>
      <Grid container spacing={1} justifyContent={'center'} className={classes.grid}>
        {/* Main Title Card */}
        <Grid item xs={10}>
          <Card>
            <CardContent>
              <Typography variant="h3">NDEF Applet</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {t('ndef.description')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Current Message Card */}
        {currentMessage && (
          <Grid item xs={10}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>{t('ndef.current_message')}</Typography>
                <Typography variant="body2" style={{wordBreak: 'break-all', fontFamily: 'monospace'}}>
                  {currentMessage}
                </Typography>
                <Box mt={2}>
                  <Chip label={`${t('ndef.current_size', {size: messageSize})}`} size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Max Size Info Card */}
        <Grid item xs={10}>
          <Box className={classes.infoBox}>
            <Typography variant="body2">{t('ndef.max_size')}</Typography>
          </Box>
        </Grid>
        
        {/* Message Configuration Card */}
        <Grid item xs={10}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>Message Type</Typography>
              <FormControl fullWidth>
                <InputLabel>{t('ndef.message_type')}</InputLabel>
                <Select
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value)}
                >
                  <MenuItem value={MESSAGE_TYPES.URL}>
                    <LinkIcon fontSize="small" style={{marginRight: 8}} />
                    {t('ndef.url')}
                  </MenuItem>
                  <MenuItem value={MESSAGE_TYPES.TEXT}>
                    <TextFieldsIcon fontSize="small" style={{marginRight: 8}} />
                    {t('ndef.text')}
                  </MenuItem>
                  <MenuItem value={MESSAGE_TYPES.CUSTOM}>
                    <CodeIcon fontSize="small" style={{marginRight: 8}} />
                    {t('ndef.custom')}
                  </MenuItem>
                </Select>
              </FormControl>
              
              <Box mt={2}>
                {messageType === MESSAGE_TYPES.URL && (
                  <TextField
                    fullWidth
                    label={t('ndef.url_address')}
                    value={urlAddress}
                    onChange={(e) => setUrlAddress(e.target.value)}
                    placeholder="https://example.com"
                  />
                )}
                
                {messageType === MESSAGE_TYPES.TEXT && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t('ndef.language')}
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        placeholder="en"
                      />
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <TextField
                        fullWidth
                        label={t('ndef.text_content')}
                        value={textContent}
                        onChange={(e) => setTextContent(e.target.value)}
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Grid>
                )}
                
                {messageType === MESSAGE_TYPES.CUSTOM && (
                  <TextField
                    fullWidth
                    label={t('ndef.hex_data')}
                    value={customHex}
                    onChange={(e) => setCustomHex(e.target.value)}
                    multiline
                    rows={4}
                    placeholder="D10155046361..."
                    helperText="Enter hex data without spaces"
                  />
                )}
              </Box>
              
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={readOnly}
                      onChange={toggleReadOnly}
                    />
                  }
                  label={`${t('ndef.readonly')} - ${t('ndef.readonly_help')}`}
                />
              </Box>
              
              <Box mt={2}>
                <Button onClick={writeNdefMessage} variant="contained" color="primary" style={{marginRight: 8}}>
                  {t('ndef.write_message')}
                </Button>
                <Button onClick={readNdefMessage} variant="outlined" color="primary" style={{marginRight: 8}}>
                  {t('ndef.read_message')}
                </Button>
                <Button onClick={resetNdef} variant="outlined" color="secondary">
                  {t('ndef.reset')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}

